import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const PaintingPackages = () => {
  useEffect(() => {
    analytics.track('Template Page View', { template: 'Painting Quote Template' });
  }, []);

  return (
    <SEOPageLayout
      title="Free Painting Quote Template - Tiered Options (Good/Better/Best) | QuoteTier"
      description="Free painting contractor quote template with Good/Better/Best scope tiers. From one accent wall to a full interior repaint, all priced side-by-side."
      canonical="/templates/painting-packages-template"
      ogTitle="Free Painting Quote Template"
      ogDescription="Painters quoting one room at a time miss 'while you're here' upsells. Three-tier quotes capture them automatically. Template included."
    >
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              Free <span className="text-primary font-bold">Painting Quote Template</span>
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              From one accent wall to a full interior repaint — the quote structure that captures "while you're here" upsells before the prep tarps come out.
            </p>
            <div className="max-w-2xl mx-auto mb-10 p-5 border border-gray-200 rounded-lg bg-gray-50 text-left">
              <p className="font-jetbrains text-sm md:text-base text-gray-700 leading-relaxed">
                Scope creep mid-paint-job is the worst conversation. Putting single-room, multi-room, and full-interior tiers in the quote surfaces the "while you're here" discussion upfront — and customers who see the bulk discount usually expand the scope before you've even loaded the van.
              </p>
            </div>
            <div className="font-instrument mt-8 flex flex-col sm:flex-row gap-6 justify-center items-center mb-3">
              <Link to="/signup">
                <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Use This Template Free
                </Button>
              </Link>
            </div>
            <p className="font-jetbrains text-sm text-gray-500 mb-6">Free account — no credit card required.</p>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Sample Tiers</h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">1 Room</CardTitle><CardDescription>The Budget Option</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-600 list-disc list-inside">
                      <li>1 room / up to 400 sq ft</li>
                      <li>2 coats + ceiling</li>
                      <li>Basic prep & masking</li>
                  </ul>
              </CardContent>
            </Card>
            <Card className="border border-black bg-black text-white">
              <CardHeader><CardTitle className="font-instrument text-xl text-white">Multi-Room</CardTitle><CardDescription className="text-gray-300">Best Seller</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-300 list-disc list-inside">
                      <li>Up to 5 rooms / 2,000 sq ft</li>
                      <li>Premium paint included</li>
                      <li>Wall repair & patching</li>
                      <li>Trim & baseboards</li>
                  </ul>
              </CardContent>
            </Card>
            <Card className="border border-black">
              <CardHeader><CardTitle className="font-instrument text-xl">Full Interior</CardTitle><CardDescription>Complete Makeover</CardDescription></CardHeader>
              <CardContent>
                  <ul className="font-jetbrains text-sm text-gray-600 list-disc list-inside">
                      <li>Full interior / unlimited rooms</li>
                      <li>Designer colour consultation</li>
                      <li>Cabinet & door painting</li>
                      <li>2-year touch-up guarantee</li>
                  </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Start Using This Template Now</h2>
          <Link to="/signup">
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Get Free Access
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default PaintingPackages;
